import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Monitor, 
    Users, 
    DollarSign, 
    Clock, 
    CheckCircle, 
    XCircle, 
    AlertTriangle,
    Play,
    Loader2,
    TrendingUp,
    Banknote,
    CreditCard,
    Smartphone,
    Eye,
    RefreshCw,
    Calculator
} from 'lucide-react';
import { cn } from '../lib/utils';

const CashSupervisionPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [registers, setRegisters] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [pendingValidations, setPendingValidations] = useState([]);
    const [sessionHistory, setSessionHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('registers');
    
    // Validation dialog
    const [validationDialog, setValidationDialog] = useState({ open: false, session: null });
    const [validationNotes, setValidationNotes] = useState('');
    const [validating, setValidating] = useState(false);
    
    // Open session dialog
    const [openDialog, setOpenDialog] = useState({ open: false, register: null });
    const [openingCash, setOpeningCash] = useState('');
    const [opening, setOpening] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const isAdmin = user?.role === 'ADMIN';
    const isManager = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';

    const fetchData = useCallback(async () => {
        if (!isManager) return;
        
        try {
            setLoading(true);
            const [registersRes, activeRes, pendingRes, historyRes, employeesRes] = await Promise.all([
                api().get('/pos/registers'),
                api().get('/pos/cash-sessions/active'),
                api().get('/pos/cash-sessions/pending-validation'),
                api().get('/pos/cash-session/history?limit=20'),
                api().get('/staff')
            ]);
            
            setRegisters(registersRes.data || []);
            setActiveSessions(activeRes.data || []);
            setPendingValidations(pendingRes.data || []);
            setSessionHistory(historyRes.data || []);
            // Filter employees who can be assigned to a register
            const eligibleEmployees = (employeesRes.data || []).filter(e => 
                ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CAISSIER', 'SERVEUR', 'EMPLOYE'].includes(e.role)
            );
            setEmployees(eligibleEmployees);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur de chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api, isManager]);

    useEffect(() => {
        fetchData();
        // Refresh every 30 seconds
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleOpenSession = async () => {
        const isAdminRegister = openDialog.register?.is_admin_register || openDialog.register?.no_opening_cash_required;
        
        // Validation du fond de caisse (sauf pour caisse admin)
        if (!isAdminRegister && (!openingCash || parseFloat(openingCash) < 0)) {
            toast.error('Veuillez entrer un montant valide');
            return;
        }

        try {
            setOpening(true);
            
            const cashAmount = isAdminRegister ? 0 : parseFloat(openingCash);
            
            // Build request data
            const requestData = {
                opening_cash: cashAmount
            };
            
            // Add assigned employee if selected
            if (selectedEmployee) {
                requestData.assigned_user_id = selectedEmployee.id;
            }
            
            // Si une caisse spécifique est sélectionnée
            if (openDialog.register) {
                await api().post(`/pos/registers/${openDialog.register.id}/open-session`, requestData);
                const assigneeMsg = selectedEmployee ? ` pour ${selectedEmployee.name}` : '';
                toast.success(`Caisse "${openDialog.register.name}" ouverte${assigneeMsg}`);
            } else {
                requestData.notes = 'Ouverture par direction';
                await api().post('/pos/cash-session/open', requestData);
                toast.success('Caisse ouverte avec succès');
            }
            
            setOpenDialog({ open: false, register: null });
            setOpeningCash('');
            setSelectedEmployee(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture');
        } finally {
            setOpening(false);
        }
    };
    
    // Vérifier si une caisse a une session active
    const getRegisterSession = (registerId) => {
        return activeSessions.find(s => s.register_id === registerId);
    };
    
    // Icône selon le type de caisse
    const getRegisterIcon = (register) => {
        // Si la caisse a une icône personnalisée, l'utiliser
        if (register.icon) return register.icon;
        
        // Sinon, selon le sector
        switch (register.sector) {
            case 'bar': return '🍸';
            case 'admin': return '👑';
            case 'emporter': return '🥡';
            case 'salle': return '🍽️';
            case 'terrasse': return '☀️';
            case 'vip': return '⭐';
            default: return '💰';
        }
    };

    const handleValidate = async (approved) => {
        if (!validationDialog.session) return;

        try {
            setValidating(true);
            await api().post(`/pos/cash-session/${validationDialog.session.id}/validate`, {
                approved,
                manager_notes: validationNotes || null
            });
            
            toast.success(approved ? 'Clôture validée' : 'Clôture rejetée - caisse réouverte');
            setValidationDialog({ open: false, session: null });
            setValidationNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setValidating(false);
        }
    };

    const goToPOS = () => {
        navigate('/pos');
    };

    const formatCurrency = (amount) => {
        return (amount || 0).toLocaleString('fr-FR') + ' F';
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString('fr-FR', {
            timeZone: 'Africa/Libreville',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isManager) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <Card className="max-w-md">
                    <CardContent className="pt-6 text-center">
                        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">Accès restreint</h2>
                        <p className="text-muted-foreground mb-4">
                            Cette page est réservée aux managers.
                        </p>
                        <Button onClick={goToPOS}>Accéder à ma caisse</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-4 px-3 sm:px-4 max-w-6xl" data-testid="cash-supervision-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                        <Monitor className="w-6 h-6 text-primary" />
                        Supervision des Caisses
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Gérez toutes les caisses et validez les clôtures
                    </p>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                    <Button onClick={() => setOpenDialog({ open: true, register: null })} className="bg-green-600 hover:bg-green-700">
                        <Play className="w-4 h-4 mr-2" />
                        Ouvrir une caisse
                    </Button>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-blue-600" />
                            <div>
                                <p className="text-2xl font-bold text-blue-700">{activeSessions.length}</p>
                                <p className="text-xs text-blue-600">Caisses ouvertes</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-600" />
                            <div>
                                <p className="text-2xl font-bold text-amber-700">{pendingValidations.length}</p>
                                <p className="text-xs text-amber-600">En attente</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            <div>
                                <p className="text-2xl font-bold text-green-700">
                                    {formatCurrency(activeSessions.reduce((sum, s) => sum + (s.total_sales || 0), 0))}
                                </p>
                                <p className="text-xs text-green-600">Ventes du jour</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-600" />
                            <div>
                                <p className="text-2xl font-bold text-purple-700">
                                    {activeSessions.reduce((sum, s) => sum + (s.orders_count || 0), 0)}
                                </p>
                                <p className="text-xs text-purple-600">Commandes</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending Validations Alert */}
            {pendingValidations.length > 0 && (
                <Card className="mb-6 border-amber-300 bg-amber-50">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                            <AlertTriangle className="w-5 h-5" />
                            {pendingValidations.length} clôture(s) en attente de validation
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {pendingValidations.map((session) => (
                                <div 
                                    key={session.id}
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                            <Calculator className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{session.cashier_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {session.date} - Fermée à {formatTime(session.closed_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className="font-bold">{formatCurrency(session.closing_cash)}</p>
                                            <p className={cn(
                                                "text-sm",
                                                session.difference >= 0 ? "text-green-600" : "text-red-600"
                                            )}>
                                                Écart: {session.difference >= 0 ? '+' : ''}{formatCurrency(session.difference)}
                                            </p>
                                        </div>
                                        <Button 
                                            size="sm"
                                            onClick={() => setValidationDialog({ open: true, session })}
                                        >
                                            Valider
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="registers" className="gap-2">
                        <CreditCard className="w-4 h-4" />
                        Caisses ({registers.length})
                    </TabsTrigger>
                    <TabsTrigger value="active" className="gap-2">
                        <Monitor className="w-4 h-4" />
                        Sessions actives ({activeSessions.length})
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-2">
                        <Clock className="w-4 h-4" />
                        Historique
                    </TabsTrigger>
                </TabsList>

                {/* Registers List */}
                <TabsContent value="registers">
                    {registers.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <CreditCard className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">Aucune caisse configurée</h3>
                                <p className="text-muted-foreground">
                                    Contactez l'administrateur pour créer des caisses
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {registers.map((register) => {
                                const session = getRegisterSession(register.id);
                                const isOpen = !!session;
                                const isAdminRegister = register.is_admin_register || register.no_opening_cash_required;
                                
                                return (
                                    <Card 
                                        key={register.id} 
                                        className={cn(
                                            "overflow-hidden cursor-pointer transition-all hover:shadow-lg",
                                            isOpen ? "ring-2 ring-green-500" : "hover:ring-2 hover:ring-primary/50",
                                            isAdminRegister && "border-purple-300"
                                        )}
                                        onClick={() => isOpen ? navigate('/pos') : setOpenDialog({ open: true, register })}
                                    >
                                        <div className={cn(
                                            "h-2", 
                                            isOpen ? "bg-green-500" : isAdminRegister ? "bg-purple-400" : "bg-gray-200"
                                        )} />
                                        <CardContent className="pt-6 pb-4 text-center">
                                            <div className="text-4xl mb-3">{getRegisterIcon(register)}</div>
                                            <h3 className="font-bold text-lg mb-1">{register.name}</h3>
                                            <p className="text-sm text-muted-foreground capitalize mb-1">{register.sector}</p>
                                            {isAdminRegister && (
                                                <Badge variant="outline" className="text-purple-600 border-purple-300 text-xs mb-2">
                                                    Tous droits
                                                </Badge>
                                            )}
                                            
                                            {isOpen ? (
                                                <div className="space-y-2">
                                                    <Badge className="bg-green-500">Ouverte</Badge>
                                                    <p className="text-sm text-muted-foreground">
                                                        {session.cashier_name}
                                                    </p>
                                                    <p className="text-lg font-bold text-green-600">
                                                        {formatCurrency(session.total_sales || 0)}
                                                    </p>
                                                    <Button 
                                                        size="sm" 
                                                        className="w-full mt-2"
                                                        onClick={(e) => { e.stopPropagation(); navigate('/pos'); }}
                                                    >
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        Accéder
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <Badge variant="outline" className="text-muted-foreground">Fermée</Badge>
                                                    <Button 
                                                        size="sm" 
                                                        className="w-full mt-2 bg-green-600 hover:bg-green-700"
                                                        onClick={(e) => { e.stopPropagation(); setOpenDialog({ open: true, register }); }}
                                                    >
                                                        <Play className="w-4 h-4 mr-2" />
                                                        Ouvrir
                                                    </Button>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* Active Sessions */}
                <TabsContent value="active">
                    {activeSessions.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <Monitor className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">Aucune caisse ouverte</h3>
                                <p className="text-muted-foreground mb-4">
                                    Sélectionnez une caisse dans l'onglet "Caisses" pour l'ouvrir
                                </p>
                                <Button onClick={() => setActiveTab('registers')}>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Voir les caisses
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {activeSessions.map((session) => (
                                <Card key={session.id} className="overflow-hidden">
                                    <div className="h-1 bg-green-500" />
                                    <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                                    <Users className="w-6 h-6 text-green-600" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg">{session.cashier_name}</CardTitle>
                                                    <CardDescription>@{session.cashier_username}</CardDescription>
                                                </div>
                                            </div>
                                            <Badge className="bg-green-500">Active</Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">Ouverture</span>
                                            <span className="font-medium">{formatTime(session.opened_at)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">Fond de caisse</span>
                                            <span className="font-medium">{formatCurrency(session.opening_cash)}</span>
                                        </div>
                                        
                                        <div className="border-t pt-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2 text-sm">
                                                    <Banknote className="w-4 h-4 text-green-600" />
                                                    Espèces
                                                </span>
                                                <span className="font-medium">{formatCurrency(session.cash_sales)}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2 text-sm">
                                                    <Smartphone className="w-4 h-4 text-blue-600" />
                                                    Mobile Money
                                                </span>
                                                <span className="font-medium">{formatCurrency(session.mobile_sales)}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2 text-sm">
                                                    <CreditCard className="w-4 h-4 text-purple-600" />
                                                    Carte
                                                </span>
                                                <span className="font-medium">{formatCurrency(session.card_sales)}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="border-t pt-3">
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold">Total ventes</span>
                                                <span className="text-xl font-bold text-green-600">{formatCurrency(session.total_sales)}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm mt-1">
                                                <span className="text-muted-foreground">Espèces attendues</span>
                                                <span className="font-medium">{formatCurrency(session.expected_cash)}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-muted-foreground">Commandes</span>
                                                <span className="font-medium">{session.orders_count}</span>
                                            </div>
                                        </div>
                                        
                                        <Button 
                                            className="w-full mt-2" 
                                            variant="outline"
                                            onClick={goToPOS}
                                        >
                                            <Eye className="w-4 h-4 mr-2" />
                                            Accéder à la caisse
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* History */}
                <TabsContent value="history">
                    <Card>
                        <CardContent className="p-0">
                            <ScrollArea className="h-[500px]">
                                <div className="divide-y">
                                    {sessionHistory.map((session) => (
                                        <div key={session.id} className="p-4 hover:bg-muted/50">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={cn(
                                                        "w-10 h-10 rounded-full flex items-center justify-center",
                                                        session.status === 'closed' ? "bg-green-100" : 
                                                        session.status === 'pending_validation' ? "bg-amber-100" : "bg-blue-100"
                                                    )}>
                                                        {session.status === 'closed' ? (
                                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                                        ) : session.status === 'pending_validation' ? (
                                                            <Clock className="w-5 h-5 text-amber-600" />
                                                        ) : (
                                                            <Monitor className="w-5 h-5 text-blue-600" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{session.date}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {session.cashier_name || 'Caissier'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold">{formatCurrency(session.total_sales)}</p>
                                                    {session.difference !== null && (
                                                        <p className={cn(
                                                            "text-sm",
                                                            session.difference >= 0 ? "text-green-600" : "text-red-600"
                                                        )}>
                                                            Écart: {session.difference >= 0 ? '+' : ''}{formatCurrency(session.difference)}
                                                        </p>
                                                    )}
                                                    <Badge variant={
                                                        session.status === 'closed' ? 'default' :
                                                        session.status === 'pending_validation' ? 'warning' : 'secondary'
                                                    } className="mt-1">
                                                        {session.status === 'closed' ? 'Validée' :
                                                         session.status === 'pending_validation' ? 'En attente' : 'Ouverte'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {sessionHistory.length === 0 && (
                                        <div className="p-8 text-center text-muted-foreground">
                                            Aucun historique disponible
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Open Session Dialog */}
            <Dialog open={openDialog.open} onOpenChange={(open) => setOpenDialog({ open, register: openDialog.register })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Play className="w-5 h-5 text-green-600" />
                            Ouvrir {openDialog.register ? `"${openDialog.register.name}"` : 'une caisse'}
                        </DialogTitle>
                        {!(openDialog.register?.is_admin_register || openDialog.register?.no_opening_cash_required) && (
                            <DialogDescription>
                                {openDialog.register ? 'Entrez le montant du fond de caisse pour démarrer' : 'Sélectionnez une caisse à ouvrir'}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    
                    {/* Register Selection when no register is pre-selected */}
                    {!openDialog.register && (
                        <div className="py-2">
                            <label className="text-sm font-medium mb-2 block">Sélectionner une caisse</label>
                            <Select onValueChange={(value) => {
                                const selectedRegister = registers.find(r => r.id === value);
                                setOpenDialog({ open: true, register: selectedRegister });
                            }}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choisir une caisse..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {registers.filter(r => !activeSessions.some(s => s.register_id === r.id)).map(register => (
                                        <SelectItem key={register.id} value={register.id}>
                                            <div className="flex items-center gap-2">
                                                <span>{getRegisterIcon(register)}</span>
                                                <span>{register.name}</span>
                                                <span className="text-muted-foreground text-xs">({register.sector})</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    
                    {openDialog.register && (
                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <span className="text-3xl">{getRegisterIcon(openDialog.register)}</span>
                            <div>
                                <p className="font-bold">{openDialog.register.name}</p>
                                <p className="text-sm text-muted-foreground capitalize">{openDialog.register.sector}</p>
                                {(openDialog.register.is_admin_register || openDialog.register.no_opening_cash_required) && (
                                    <Badge className="mt-1 bg-purple-500">Pas de fond de caisse requis</Badge>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Employee Assignment (optional) */}
                    {openDialog.register && isManager && (
                        <div className="py-2">
                            <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                                <Users className="w-4 h-4 text-muted-foreground" />
                                Attribuer à un employé (optionnel)
                            </label>
                            <Select 
                                value={selectedEmployee?.id || 'self'} 
                                onValueChange={(value) => {
                                    if (value === 'self') {
                                        setSelectedEmployee(null);
                                    } else {
                                        const emp = employees.find(e => e.id === value);
                                        setSelectedEmployee(emp);
                                    }
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Moi-même (par défaut)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="self">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">Moi-même ({user?.name})</span>
                                        </div>
                                    </SelectItem>
                                    {employees.filter(e => e.id !== user?.id).map(emp => (
                                        <SelectItem key={emp.id} value={emp.id}>
                                            <div className="flex items-center gap-2">
                                                <span>{emp.name}</span>
                                                <Badge variant="outline" className="text-xs">{emp.role}</Badge>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedEmployee && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    La caisse sera attribuée à {selectedEmployee.name}
                                </p>
                            )}
                        </div>
                    )}
                    
                    {!(openDialog.register?.is_admin_register || openDialog.register?.no_opening_cash_required) && (
                        <div className="py-2">
                            <label className="text-sm font-medium mb-2 block">Fond de caisse (F CFA)</label>
                            <Input
                                type="number"
                                placeholder="Ex: 50000"
                                value={openingCash}
                                onChange={(e) => setOpeningCash(e.target.value)}
                                className="text-lg h-12"
                            />
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenDialog({ open: false, register: null })}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleOpenSession} 
                            disabled={opening || (!(openDialog.register?.is_admin_register || openDialog.register?.no_opening_cash_required) && !openingCash)}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {opening ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                            Ouvrir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Validation Dialog */}
            <Dialog open={validationDialog.open} onOpenChange={(open) => !open && setValidationDialog({ open: false, session: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-amber-600" />
                            Valider la clôture de caisse
                        </DialogTitle>
                    </DialogHeader>
                    
                    {validationDialog.session && (
                        <div className="py-4 space-y-4">
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Caissier</span>
                                    <span className="font-medium">{validationDialog.session.cashier_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Date</span>
                                    <span className="font-medium">{validationDialog.session.date}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Fermée à</span>
                                    <span className="font-medium">{formatTime(validationDialog.session.closed_at)}</span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-blue-50 rounded-lg text-center">
                                    <p className="text-sm text-blue-600">Espèces attendues</p>
                                    <p className="text-xl font-bold text-blue-700">
                                        {formatCurrency(validationDialog.session.expected_cash)}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-50 rounded-lg text-center">
                                    <p className="text-sm text-green-600">Espèces comptées</p>
                                    <p className="text-xl font-bold text-green-700">
                                        {formatCurrency(validationDialog.session.closing_cash)}
                                    </p>
                                </div>
                            </div>
                            
                            <div className={cn(
                                "p-4 rounded-lg text-center",
                                validationDialog.session.difference >= 0 ? "bg-green-100" : "bg-red-100"
                            )}>
                                <p className="text-sm">Écart</p>
                                <p className={cn(
                                    "text-2xl font-bold",
                                    validationDialog.session.difference >= 0 ? "text-green-700" : "text-red-700"
                                )}>
                                    {validationDialog.session.difference >= 0 ? '+' : ''}
                                    {formatCurrency(validationDialog.session.difference)}
                                </p>
                            </div>
                            
                            {validationDialog.session.closing_notes && (
                                <div className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-sm font-medium mb-1">Notes du caissier:</p>
                                    <p className="text-sm text-muted-foreground">{validationDialog.session.closing_notes}</p>
                                </div>
                            )}
                            
                            <div>
                                <label className="text-sm font-medium mb-2 block">Notes du manager (optionnel)</label>
                                <Textarea
                                    value={validationNotes}
                                    onChange={(e) => setValidationNotes(e.target.value)}
                                    placeholder="Commentaires sur la clôture..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="destructive" 
                            onClick={() => handleValidate(false)}
                            disabled={validating}
                            className="w-full sm:w-auto"
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rejeter (recompter)
                        </Button>
                        <Button 
                            onClick={() => handleValidate(true)}
                            disabled={validating}
                            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                        >
                            {validating ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Valider
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashSupervisionPage;
